import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import { getPeriodRange, toISTDateString } from '@/lib/periods';

/**
 * Live "today so far" totals from marketing.outlet_sales_live, populated by the
 * petpooja-sales-fetch Edge Function (Petpooja get_orders_api, every ~30 min).
 *
 * This is intentionally separate from useDayMetric / mv_outlet_kpi_daily: that
 * rollup only covers dates < current_date, so it can never show today. Here we
 * read the intraday running totals for the current IST date directly.
 */
export interface LiveTodayResult {
  net_sales: number;
  orders: number;
  covers: number;
  avg_ticket: number;
  /** Most recent sync across the summed outlets, or null if nothing yet. */
  syncedAt: string | null;
  /** True when at least one outlet has a row for today. */
  hasData: boolean;
}

interface LiveRow {
  outlet_id: string;
  net_sales: number | string | null;
  orders: number | string | null;
  covers: number | string | null;
  synced_at: string | null;
}

export function useLiveToday(outletId: string | 'all') {
  const today = toISTDateString(getPeriodRange('today').start);

  return useQuery<LiveTodayResult>({
    queryKey: ['live-today', outletId, today],
    queryFn: async () => {
      let q = marketing
        .from('outlet_sales_live')
        .select('outlet_id, net_sales, orders, covers, synced_at')
        .eq('date', today);
      if (outletId !== 'all') q = q.eq('outlet_id', outletId);

      const { data, error } = await q;
      if (error) throw error;

      const rows = (data ?? []) as unknown as LiveRow[];
      let net_sales = 0;
      let orders = 0;
      let covers = 0;
      let syncedAt: string | null = null;
      for (const r of rows) {
        net_sales += Number(r.net_sales ?? 0);
        orders += Number(r.orders ?? 0);
        covers += Number(r.covers ?? 0);
        if (r.synced_at && (!syncedAt || r.synced_at > syncedAt)) syncedAt = r.synced_at;
      }

      return {
        net_sales,
        orders,
        covers,
        avg_ticket: orders > 0 ? net_sales / orders : 0,
        syncedAt,
        hasData: rows.length > 0,
      };
    },
    // Live intraday numbers — refresh often and on focus.
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}
